/**
 * UsageCard Component
 *
 * Displays AI usage summary (cost, token counts, per-model breakdown)
 * for the current month. Loaded via TanStack Query.
 */

import { useQuery } from '@tanstack/react-query';
import { DollarSign } from 'lucide-react';
import { getUsageSummary } from '../api/usage';

function formatCost(usd: number): string {
  if (usd === 0) return '$0.00';
  if (usd < 0.01) return `< $0.01`;
  return `$${usd.toFixed(2)}`;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

function friendlyModel(model: string): string {
  // Strip vendor prefix and version suffix for display
  return model
    .replace(/^claude-/, '')
    .replace(/-\d{8}$/, '')
    .replace(/-\d{4,}$/, '');
}

export function UsageCard() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['usage-summary', 'month'],
    queryFn: () => getUsageSummary('month'),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  return (
    <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-4">
      <div className="flex items-center gap-2">
        <DollarSign className="w-5 h-5 text-primary-600 dark:text-primary-400" />
        <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
          Usage This Month
        </h2>
      </div>

      {isLoading && (
        <div className="space-y-2 animate-pulse">
          <div className="h-8 bg-surface-hover-light dark:bg-surface-hover-dark rounded w-24" />
          <div className="h-4 bg-surface-hover-light dark:bg-surface-hover-dark rounded w-40" />
          <div className="h-4 bg-surface-hover-light dark:bg-surface-hover-dark rounded w-32" />
        </div>
      )}

      {isError && (
        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
          Unable to load usage data.
        </p>
      )}

      {data && !isLoading && (
        <>
          {/* Total cost */}
          <div>
            <p className="text-3xl font-bold text-text-primary-light dark:text-text-primary-dark">
              {formatCost(data.totalCostUsd)}
            </p>
            <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark mt-1">
              {formatTokens(data.totalInputTokens)} in &middot;{' '}
              {formatTokens(data.totalOutputTokens)} out &middot; period: {data.period}
            </p>
          </div>

          {/* Per-model breakdown */}
          {data.byModel.length === 0 ? (
            <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark italic">
              No usage data yet
            </p>
          ) : (
            <ul className="space-y-2">
              {data.byModel.map((row) => (
                <li
                  key={row.model}
                  className="flex items-center justify-between text-sm"
                >
                  <div>
                    <span className="font-mono text-text-secondary-light dark:text-text-secondary-dark">
                      {friendlyModel(row.model)}
                    </span>
                    <span className="ml-2 text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                      {row.callCount} {row.callCount === 1 ? 'call' : 'calls'}
                    </span>
                  </div>
                  <span className="font-medium text-text-primary-light dark:text-text-primary-dark tabular-nums">
                    {formatCost(row.costUsd)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
